import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchCorporateCatalystsSummary } from '../api';
import { StockCatalystSummary } from '../types';
import { isPdfUrl } from '../utils/feedDeduplication';

const CATALYSTS_STORAGE_KEY = 'niftysutra.corporate-catalysts-summary';
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

const readStoredCatalysts = (): { map: Record<string, StockCatalystSummary[]>; timestamp: number } | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(CATALYSTS_STORAGE_KEY) || window.localStorage.getItem(CATALYSTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.map && typeof parsed.timestamp === 'number') {
      return parsed;
    }
  } catch {
    // Ignore storage parse errors
  }
  return null;
};

const writeStoredCatalysts = (map: Record<string, StockCatalystSummary[]>) => {
  if (typeof window === 'undefined') return;
  try {
    const payload = JSON.stringify({ map, timestamp: Date.now() });
    window.sessionStorage.setItem(CATALYSTS_STORAGE_KEY, payload);
  } catch {
    // Ignore storage quota errors
  }
};

// Module-level singleton cache initialized synchronously from storage if still fresh
const initialStored = readStoredCatalysts();
let cachedCatalystsBySymbol: Record<string, StockCatalystSummary[]> | null =
  initialStored && Date.now() - initialStored.timestamp < CACHE_TTL_MS ? initialStored.map : null;
let pendingFetchPromise: Promise<Record<string, StockCatalystSummary[]> | null> | null = null;
let lastFetchTime = initialStored ? initialStored.timestamp : 0;

export interface UseStockCatalystsResult {
  isAdmin: boolean;
  loading: boolean;
  catalystsMap: Record<string, StockCatalystSummary[]>;
  getCatalysts: (symbol?: string | null) => StockCatalystSummary[] | undefined;
  getPrimaryCatalyst: (symbol?: string | null) => StockCatalystSummary | null;
  hasCatalyst: (symbol?: string | null) => boolean;
}

const isMaterialCategory = (cat?: string): boolean => {
  if (!cat) return false;
  const c = cat.toLowerCase();
  return (
    c.includes('order') ||
    c.includes('fund') ||
    c.includes('preferential') ||
    c.includes('capex') ||
    c.includes('acquisition') ||
    c.includes('amalgamation') ||
    c.includes('bonus') ||
    c.includes('split')
  );
};

const scoreCatalyst = (c: StockCatalystSummary): number => {
  let score = 0;
  if (isPdfUrl(c.AttachmentUrl)) score += 100;
  if (isMaterialCategory(c.SubCategory || c.Category)) score += 50;
  if (c.Amount) score += 20;
  const timestamp = new Date(c.Date).getTime();
  if (!Number.isNaN(timestamp)) {
    score += timestamp / 1e12;
  }
  return score;
};

const sortCatalystsMap = (
  rawMap: Record<string, StockCatalystSummary[]>
): Record<string, StockCatalystSummary[]> => {
  const result: Record<string, StockCatalystSummary[]> = {};
  for (const [sym, items] of Object.entries(rawMap)) {
    if (Array.isArray(items)) {
      const sorted = [...items].sort((a, b) => scoreCatalyst(b) - scoreCatalyst(a));
      const clean = sym.trim().toUpperCase().replace(/\.(NS|BO)$/i, '');
      result[clean] = sorted;
      result[`${clean}.NS`] = sorted;
    }
  }
  return result;
};

/**
 * Asynchronously fetch or retrieve corporate catalysts map, leveraging memory & session cache
 */
export const getCorporateCatalystsMapAsync = async (): Promise<Record<string, StockCatalystSummary[]>> => {
  const now = Date.now();
  if (cachedCatalystsBySymbol && now - lastFetchTime < CACHE_TTL_MS) {
    return cachedCatalystsBySymbol;
  }

  const stored = readStoredCatalysts();
  if (stored && now - stored.timestamp < CACHE_TTL_MS) {
    cachedCatalystsBySymbol = stored.map;
    lastFetchTime = stored.timestamp;
    return stored.map;
  }

  if (pendingFetchPromise) {
    const res = await pendingFetchPromise;
    return res ?? {};
  }

  pendingFetchPromise = (async () => {
    try {
      const index = await fetchCorporateCatalystsSummary();
      const map = index?.CatalystsBySymbol ?? {};
      const sorted = sortCatalystsMap(map);
      cachedCatalystsBySymbol = sorted;
      lastFetchTime = Date.now();
      writeStoredCatalysts(sorted);
      return sorted;
    } catch {
      return cachedCatalystsBySymbol ?? {};
    } finally {
      pendingFetchPromise = null;
    }
  })();

  const res = await pendingFetchPromise;
  return res ?? {};
};

/**
 * Direct async lookup for a stock's key catalysts with zero per-stock overhead
 */
export const getStockCatalystsAsync = async (symbol: string): Promise<StockCatalystSummary[]> => {
  const sym = (symbol || '').trim().toUpperCase().replace(/\.(NS|BO)$/i, '');
  if (!sym) return [];
  const map = await getCorporateCatalystsMapAsync();
  return map[sym] ?? map[`${sym}.NS`] ?? [];
};

export const useStockCatalysts = (): UseStockCatalystsResult => {
  const { isAdmin } = useAuth();
  const [catalystsMap, setCatalystsMap] = useState<Record<string, StockCatalystSummary[]>>(
    cachedCatalystsBySymbol ?? {}
  );
  const [loading, setLoading] = useState<boolean>(!cachedCatalystsBySymbol);

  useEffect(() => {
    const now = Date.now();
    if (cachedCatalystsBySymbol && now - lastFetchTime < CACHE_TTL_MS) {
      setCatalystsMap(cachedCatalystsBySymbol);
      setLoading(false);
      return;
    }

    let isMounted = true;
    void getCorporateCatalystsMapAsync().then((map) => {
      if (isMounted && map) {
        setCatalystsMap(map);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const getCatalysts = useCallback(
    (symbol?: string | null): StockCatalystSummary[] | undefined => {
      if (!symbol) return undefined;
      const cleanSym = symbol.trim().toUpperCase().replace(/\.(NS|BO)$/i, '');
      return catalystsMap[cleanSym] ?? catalystsMap[`${cleanSym}.NS`];
    },
    [catalystsMap]
  );

  const getPrimaryCatalyst = useCallback(
    (symbol?: string | null): StockCatalystSummary | null => {
      const list = getCatalysts(symbol);
      if (!list || list.length === 0) return null;
      const withPdf = list.find((c) => isPdfUrl(c.AttachmentUrl));
      return withPdf || list[0];
    },
    [getCatalysts]
  );

  const hasCatalyst = useCallback(
    (symbol?: string | null): boolean => {
      const list = getCatalysts(symbol);
      return Boolean(list && list.length > 0);
    },
    [getCatalysts]
  );

  return {
    isAdmin,
    loading,
    catalystsMap,
    getCatalysts,
    getPrimaryCatalyst,
    hasCatalyst,
  };
};

export default useStockCatalysts;
